import React, { useState } from 'react';
import logo from '../../assets/Universe_logo_transparent.svg';
import HamburgerMenu from './HamburgerMenu';
import './AppHeader.css';
import { useNavigation } from '../../contexts/NavigationContext';

/**
 * Shared AppHeader Component
 * Replaces duplicated header/logo/hamburger code across 10+ components
 *
 * @param {Object} userData - User data object containing enrolled_university
 * @param {Function} onLogout - Callback for logout
 * @param {Boolean} isLoggingOut - Loading state for logout
 * @param {String} logoutError - Error message for logout
 * @param {Function} onClearLogoutError - Clear logout error
 * @param {Function} onNavigateToLanding - Navigate to landing page (optional, uses context if not provided)
 * @param {Function} onNavigateToMyPosts - Navigate to my posts (optional, uses context if not provided)
 * @param {Function} onNavigateToMeinMarktplatz - Navigate to marketplace (optional, uses context if not provided)
 * @param {Function} onNavigateToCourseReview - Navigate to course review (optional, uses context if not provided)
 * @param {React.ReactNode} children - Optional children to render after header (e.g., CreatePost)
 */
const AppHeader = ({
  userData,
  onLogout,
  isLoggingOut = false,
  logoutError = null,
  onClearLogoutError,
  onNavigateToLanding,
  onNavigateToMyPosts,
  onNavigateToMeinMarktplatz,
  onNavigateToCourseReview,
  children
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigation = useNavigation();

  // Use props if provided, otherwise fall back to navigation context
  const handleNavigateToLanding = onNavigateToLanding || navigation.onNavigateToLandingPage;
  const handleNavigateToMyPosts = onNavigateToMyPosts || navigation.onNavigateToMyPosts;
  const handleNavigateToMeinMarktplatz = onNavigateToMeinMarktplatz || navigation.onNavigateToMeinMarktplatz;
  const handleNavigateToCourseReview = onNavigateToCourseReview || navigation.onNavigateToCourseReview;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuItemClick = (callback) => {
    setIsMenuOpen(false);
    callback?.();
  };

  return (
    <header className="app-header">
      <div className="header-nav">
        <button className="menu-burger" onClick={toggleMenu}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="header-logo">
          <img src={logo} alt="Universe Logo" className="app-logo" />
          <span className="logo-text">
            {userData?.enrolled_university
              ? `${userData.enrolled_university.toUpperCase()} `
              : 'UNIVERSE '}
            <span className="in-universe">IN UNIVERSE</span>
          </span>
        </div>
      </div>

      {/* Hamburger Menu Dropdown */}
      {isMenuOpen && (
        <HamburgerMenu
          onClose={toggleMenu}
          onNavigateToLanding={() => handleMenuItemClick(handleNavigateToLanding)}
          onNavigateToMyPosts={() => handleMenuItemClick(handleNavigateToMyPosts)}
          onNavigateToMeinMarktplatz={() => handleMenuItemClick(handleNavigateToMeinMarktplatz)}
          onNavigateToCourseReview={() => handleMenuItemClick(handleNavigateToCourseReview)}
          onLogout={onLogout}
          isLoggingOut={isLoggingOut}
        />
      )}

      {/* Logout Error Message - Issue #115: Changed to button for keyboard accessibility */}
      {logoutError && (
        <button
          type="button"
          className="logout-error-banner"
          onClick={onClearLogoutError}
          role="alert"
          aria-label={`${logoutError}. Click to dismiss.`}
        >
          {logoutError}
        </button>
      )}

      {/* Optional children (e.g., CreatePost component) */}
      {children}
    </header>
  );
};

export default AppHeader;
